import React, { useState } from 'react';

const GrammarUnit7Content = ({ unitData }) => {
  const [currentSection, setCurrentSection] = useState('overview');
  const [showPractice, setShowPractice] = useState(false);
  const [userAnswers, setUserAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  // Safety check for unitData and content
  if (!unitData || !unitData.content) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-100 to-green-100 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">Unit 7 - Data Loading</h1>
            <p className="text-lg text-gray-600">Unit data is being loaded...</p>
          </div>
        </div>
      </div>
    );
  }

  const content = unitData.content;

  const handleAnswerSelect = (questionIndex, selectedOption) => {
    setUserAnswers(prev => ({
      ...prev,
      [questionIndex]: selectedOption
    }));
  };

  const calculateScore = () => {
    if (!content.practiceQuestions) return { correct: 0, total: 0, percentage: 0 };
    
    let correct = 0;
    content.practiceQuestions.forEach((question, index) => {
      if (userAnswers[index] === question.correct) {
        correct++;
      }
    });
    return {
      correct,
      total: content.practiceQuestions.length,
      percentage: Math.round((correct / content.practiceQuestions.length) * 100)
    };
  };

  const renderOverview = () => {
    const section = content.overview;
    if (!section) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{section.title}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-emerald-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-emerald-800 mb-2">概念</h3>
            <p className="text-emerald-700">{section.concept}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-green-800 mb-2">何時使用</h3>
            <p className="text-green-700">{section.whenToUse}</p>
          </div>
        </div>
        
        <div className="mt-6 bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">如何使用</h3>
          <p className="text-gray-700">{section.howToUse}</p>
        </div>
        
        <div className="mt-4 bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400">
          <h3 className="text-lg font-semibold text-yellow-800 mb-2">記憶技巧</h3>
          <p className="text-yellow-700">{section.memoryTip}</p>
        </div>
      </div>
    );
  };

  const renderComplexSubjects = () => {
    const section = content.complexSubjects;
    if (!section) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{section.title}</h2>
        <p className="text-gray-700 mb-6">{section.concept}</p>
        
        {section.rules.map((rule, index) => (
          <div key={index} className="mb-6 bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">{rule.rule}</h3>
            <p className="text-gray-700 mb-3">{rule.concept}</p>
            <div className="space-y-3">
              {rule.examples.map((example, exIndex) => (
                <div key={exIndex} className="bg-white p-3 rounded border-l-4 border-emerald-400">
                  <div className="font-medium text-gray-800">• {example.en}</div>
                  <div className="text-gray-600 text-sm mt-1">{example.zh}</div>
                </div>
              ))}
            </div>
            {rule.tip && (
              <div className="mt-3 bg-blue-50 p-3 rounded border-l-4 border-blue-400">
                <p className="text-blue-700 text-sm"><strong>Tip:</strong> {rule.tip}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderInvertedSentences = () => {
    const section = content.invertedSentences;
    if (!section) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{section.title}</h2>
        <p className="text-gray-700 mb-6">{section.concept}</p>
        
        {section.rules.map((rule, index) => (
          <div key={index} className="mb-6 bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">{rule.rule}</h3>
            <div className="space-y-3">
              {rule.examples.map((example, exIndex) => (
                <div key={exIndex} className="bg-white p-3 rounded border-l-4 border-green-400">
                  <div className="font-medium text-gray-800">• {example.en}</div>
                  <div className="text-gray-600 text-sm mt-1">{example.zh}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderCompoundSubjects = () => {
    const section = content.compoundSubjects;
    if (!section) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{section.title}</h2>
        <p className="text-gray-700 mb-6">{section.concept}</p>
        
        {section.rules.map((rule, index) => (
          <div key={index} className="mb-6 bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">{rule.rule}</h3>
            <p className="text-gray-700 mb-3">{rule.concept}</p>
            <div className="space-y-3">
              {rule.examples.map((example, exIndex) => (
                <div key={exIndex} className="bg-white p-3 rounded border-l-4 border-emerald-400">
                  <div className="font-medium text-gray-800">• {example.en}</div>
                  <div className="text-gray-600 text-sm mt-1">{example.zh}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderSpecialCases = () => {
    const section = content.specialCases;
    if (!section) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{section.title}</h2>
        
        {section.rules.map((rule, index) => (
          <div key={index} className="mb-6 bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">{rule.rule}</h3>
            <p className="text-gray-700 mb-3">{rule.concept}</p>
            <div className="space-y-3">
              {rule.examples.map((example, exIndex) => (
                <div key={exIndex} className="bg-white p-3 rounded border-l-4 border-green-400">
                  <div className="font-medium text-gray-800">• {example.en}</div>
                  <div className="text-gray-600 text-sm mt-1">{example.zh}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderAdvancedPatterns = () => {
    const section = content.advancedPatterns;
    if (!section) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{section.title}</h2>
        
        {section.rules.map((rule, index) => (
          <div key={index} className="mb-6 bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">{rule.rule}</h3>
            <p className="text-gray-700 mb-3">{rule.concept}</p>
            <div className="space-y-3">
              {rule.examples.map((example, exIndex) => (
                <div key={exIndex} className="bg-white p-3 rounded border-l-4 border-emerald-400">
                  <div className="font-medium text-gray-800">• {example.en}</div>
                  <div className="text-gray-600 text-sm mt-1">{example.zh}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPracticeSection = () => {
    if (!content.practiceQuestions) return null;

    const score = showResults ? calculateScore() : null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b-3 border-blue-500 pb-3">
          練習題 - Practice Questions
        </h2>

        {!showResults ? (
          <>
            {content.practiceQuestions.map((question, qIndex) => (
              <div key={qIndex} className="bg-gray-50 p-5 rounded-lg mb-4 border border-gray-200">
                <h4 className="text-gray-800 mb-4 font-medium">
                  {qIndex + 1}. {question.question}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {question.options.map((option, oIndex) => (
                    <button
                      key={oIndex}
                      onClick={() => handleAnswerSelect(qIndex, oIndex)}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        userAnswers[qIndex] === oIndex 
                          ? 'border-emerald-500 bg-emerald-500 text-white' 
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            
            <button
              onClick={() => setShowResults(true)}
              disabled={Object.keys(userAnswers).length < content.practiceQuestions.length}
              className={`px-6 py-3 rounded-lg text-white font-medium transition-all ${
                Object.keys(userAnswers).length < content.practiceQuestions.length
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-500 hover:bg-green-600'
              }`}
            >
              提交答案 Submit Answers
            </button>
          </>
        ) : (
          <div>
            <div className={`p-5 rounded-lg mb-5 text-center ${
              score.percentage >= 70 ? 'bg-green-100' : 'bg-red-100'
            }`}>
              <h3 className={`text-xl font-bold mb-2 ${
                score.percentage >= 70 ? 'text-green-800' : 'text-red-800'
              }`}>
                測驗結果 - Test Results
              </h3>
              <p className={`text-2xl font-bold ${
                score.percentage >= 70 ? 'text-green-800' : 'text-red-800'
              }`}>
                {score.correct} / {score.total} ({score.percentage}%)
              </p>
            </div>

            {content.practiceQuestions.map((question, qIndex) => {
              const userAnswer = userAnswers[qIndex];
              const isCorrect = userAnswer === question.correct;
              
              return (
                <div key={qIndex} className={`p-4 rounded-lg mb-3 border-2 ${
                  isCorrect ? 'bg-green-100 border-green-300' : 'bg-red-100 border-red-300'
                }`}>
                  <h4 className={`font-medium mb-2 ${
                    isCorrect ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {qIndex + 1}. {question.question}
                  </h4>
                  <p className={`mb-2 ${
                    isCorrect ? 'text-green-800' : 'text-red-800'
                  }`}>
                    Your answer: {question.options[userAnswer]} {isCorrect ? '✓' : '✗'}
                  </p>
                  {!isCorrect && (
                    <p className="text-red-800 mb-2">
                      Correct answer: {question.options[question.correct]}
                    </p>
                  )}
                  <p className={`text-sm italic ${
                    isCorrect ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {question.explanation}
                  </p>
                </div>
              );
            })}

            <button
              onClick={() => {
                setShowResults(false);
                setUserAnswers({});
              }}
              className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all"
            >
              重新測驗 Retake Test
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderMemoryTips = () => {
    const section = content.memoryTips;
    if (!section) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4 border-b-3 border-yellow-500 pb-3">
          💡 {section.title}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {section.tips.map((tip, index) => (
            <div key={index} className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h4 className="font-semibold text-yellow-800 mb-2">{tip.category}</h4>
              <p className="text-yellow-700 text-sm">{tip.content}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (currentSection) {
      case 'overview':
        return renderOverview();
      case 'complexSubjects':
        return renderComplexSubjects();
      case 'invertedSentences':
        return renderInvertedSentences();
      case 'compoundSubjects':
        return renderCompoundSubjects();
      case 'specialCases':
        return renderSpecialCases();
      case 'advancedPatterns':
        return renderAdvancedPatterns();
      case 'practice':
        return renderPracticeSection();
      case 'memoryTips':
        return renderMemoryTips();
      default:
        return renderOverview();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-100 to-green-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6 text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">
            {unitData.title.english}
          </h1>
          <p className="text-lg text-gray-600 mb-6">
            {unitData.subtitle.english}
          </p>
          
          {/* Navigation */}
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            <button
              onClick={() => setCurrentSection('overview')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'overview' && !showPractice
                  ? 'border-emerald-500 bg-emerald-500 text-white'
                  : 'border-emerald-500 text-emerald-500 hover:bg-emerald-50'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setCurrentSection('complexSubjects')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'complexSubjects' && !showPractice
                  ? 'border-emerald-500 bg-emerald-500 text-white'
                  : 'border-emerald-500 text-emerald-500 hover:bg-emerald-50'
              }`}
            >
              Complex Subjects
            </button>
            <button
              onClick={() => setCurrentSection('invertedSentences')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'invertedSentences' && !showPractice
                  ? 'border-emerald-500 bg-emerald-500 text-white'
                  : 'border-emerald-500 text-emerald-500 hover:bg-emerald-50'
              }`}
            >
              Inverted Sentences
            </button>
            <button
              onClick={() => setCurrentSection('compoundSubjects')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'compoundSubjects' && !showPractice
                  ? 'border-emerald-500 bg-emerald-500 text-white'
                  : 'border-emerald-500 text-emerald-500 hover:bg-emerald-50'
              }`}
            >
              Compound Subjects
            </button>
            <button
              onClick={() => setCurrentSection('specialCases')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'specialCases' && !showPractice
                  ? 'border-emerald-500 bg-emerald-500 text-white'
                  : 'border-emerald-500 text-emerald-500 hover:bg-emerald-50'
              }`}
            >
              Special Cases
            </button>
            <button
              onClick={() => setCurrentSection('advancedPatterns')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'advancedPatterns' && !showPractice
                  ? 'border-emerald-500 bg-emerald-500 text-white'
                  : 'border-emerald-500 text-emerald-500 hover:bg-emerald-50'
              }`}
            >
              Advanced Patterns
            </button>
            <button
              onClick={() => setCurrentSection('practice')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'practice'
                  ? 'border-blue-500 bg-blue-500 text-white'
                  : 'border-blue-500 text-blue-500 hover:bg-blue-50'
              }`}
            >
              Practice
            </button>
            <button
              onClick={() => setCurrentSection('memoryTips')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'memoryTips'
                  ? 'border-yellow-500 bg-yellow-500 text-white'
                  : 'border-yellow-500 text-yellow-500 hover:bg-yellow-50'
              }`}
            >
              Memory Tips
            </button>
          </div>
        </div>

        {/* Content */}
        {renderContent()}
      </div>
    </div>
  );
};

export default GrammarUnit7Content;
